import { Fragment } from 'react';
import type { ReactNode } from 'react';

/** Согласовано с серверным normalizeForSearch для сопоставления ё/Е. Пробелы внутри строки не схлопываем — сохраняем индексы. */
export function normalizeHaystackForMatch(s: string): string {
  return s.replace(/ё/g, 'е').replace(/Ё/g, 'е');
}

/** Нормализация строки запроса (как на сервере). */
export function normalizeForSearchHighlight(s: string): string {
  return s
    .replace(/ё/g, 'е')
    .replace(/Ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Слова для подсветки (после нормализации и split). */
export function parseSearchHighlightWords(query: string): string[] {
  const n = normalizeForSearchHighlight(query);
  if (!n) return [];
  return [...new Set(n.split(/\s+/).filter(Boolean))];
}

/** Расписание: только жёлтая подсветка из токенов. */
export const SEARCH_HIGHLIGHT_CLASS_SCHEDULE = 'search-text-highlight';

/** Мессенджер (боковая панель): та же жёлтая подсветка, что `--search-highlight-bg` в tokens. */
export const SEARCH_HIGHLIGHT_CLASS_MESSENGER = SEARCH_HIGHLIGHT_CLASS_SCHEDULE;

/** Таблица проектов: жёлтый базовый класс + модификатор с красным фоном из токенов. */
export const SEARCH_HIGHLIGHT_CLASS_PROJECTS =
  'search-text-highlight search-text-highlight--projects';

/** Каталог чтецов: те же токены подсветки, что у таблицы проектов (`tokens.css`). */
export const SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG = SEARCH_HIGHLIGHT_CLASS_PROJECTS;

function mergeIntervals(intervals: Array<{ s: number; e: number }>): Array<{ s: number; e: number }> {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.s - b.s || b.e - a.e);
  const out: Array<{ s: number; e: number }> = [];
  for (const iv of sorted) {
    const last = out[out.length - 1];
    if (!last || iv.s >= last.e) out.push({ ...iv });
    else last.e = Math.max(last.e, iv.e);
  }
  return out;
}

/**
 * Подсвечивает вхождения слов из поиска в отображаемой строке (без учёта регистра).
 * @param markClassName классы для `<mark>` (по умолчанию стиль таблицы проектов).
 */
export function highlightPlainText(
  text: string | null | undefined,
  words: readonly string[],
  markClassName: string = SEARCH_HIGHLIGHT_CLASS_PROJECTS
): ReactNode {
  if (text == null || text === '' || words.length === 0) return text ?? '';

  const hay = normalizeHaystackForMatch(text).toLowerCase();
  const normWords = words
    .map((w) => normalizeForSearchHighlight(w).toLowerCase())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  const intervals: Array<{ s: number; e: number }> = [];
  for (const w of normWords) {
    let from = 0;
    while (from < hay.length) {
      const idx = hay.indexOf(w, from);
      if (idx === -1) break;
      intervals.push({ s: idx, e: idx + w.length });
      from = idx + 1;
    }
  }
  if (intervals.length === 0) return text;

  const merged = mergeIntervals(intervals);
  const parts: ReactNode[] = [];
  let pos = 0;
  let key = 0;
  for (const iv of merged) {
    if (iv.s > pos) parts.push(text.slice(pos, iv.s));
    parts.push(
      <mark key={`h-${key++}`} className={markClassName}>
        {text.slice(iv.s, iv.e)}
      </mark>
    );
    pos = iv.e;
  }
  if (pos < text.length) parts.push(text.slice(pos));
  return <Fragment>{parts}</Fragment>;
}
